"use client";

import { Carousel, CarouselContent, CarouselItem } from "@/components/carousel/carousel";
import CardContentItem from "@/components/cards/cardContent/cardContent";
import LazyMount from "@/components/utilities/LazyMount";
import type { ContentCard } from "@/types/cms";
import styles from "./CardSection.module.css";

type CardVariant = "default" | "testimonial" | "price" | "lesson";

/**
 * The shared mobile-only (`.onlySm`) card carousel — one component for the 6
 * near-identical blocks that used to be inlined across CardSection's layout
 * branches. Statically imported by CardSection (so embla is bundled once), but
 * the carousel itself is wrapped in `LazyMount` so embla only constructs/
 * measures as the section nears the viewport. The paired desktop grid/single
 * (`.hideSm`) stays server-rendered for SEO.
 */
export default function MobileCardCarousel({
  cards,
  getCardVariant,
  shadow = false,
  imageSizes,
}: {
  cards: ContentCard[];
  getCardVariant: (card: ContentCard) => CardVariant;
  shadow?: boolean;
  imageSizes?: string;
}) {
  return (
    <div className={[styles.carouselWrap, styles.onlySm, styles.carouselFullSm].join(" ")}>
      {/* Defer embla init (its slide-measurement reflow) until the carousel
          nears the viewport. On desktop this wrapper is `display:none`, so the
          IntersectionObserver never fires and embla is skipped entirely there.
          minHeight reserves space so the off-screen mount doesn't jolt scroll. */}
      <LazyMount style={{ minHeight: 300 }}>
        <Carousel>
          <CarouselContent className={styles.carouselContentSm}>
            {cards.map((card, idx) => (
              <CarouselItem
                key={card.id ?? `${idx}-${card.header || "card"}`}
                className={styles.carouselItemSm}
              >
                <CardContentItem
                  card={card}
                  shadow={shadow}
                  variant={getCardVariant(card)}
                  imageSizes={imageSizes}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </LazyMount>
    </div>
  );
}
